import {
    type FC
} from "react";
import {
    Pressable,
    Animated,
    Platform
} from "react-native";
import type IInputGroupButtonProps from "./type";
import stylesheet, {
    getInputGroupButtonRadius,
    getInputGroupButtonColors,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    usePressScale
} from "../../../../helpers/motion";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    useInputGroupAddonContext,
    useInputGroupContext
} from "../../context";
import {
    getControlMeasures,
    splitControlStyle
} from "../../../../helpers/theme/control";
import {
    INPUT_GROUP_COMPACT_SIZES
} from "../../stylesheet";
import ButtonComponent from "../../../button";
import StateLayer from "../../../stateLayer";
import InputGroupSpinner from "../spinner";
import Text from "../../../text";

const preventActionFocusSteal = (event: {
    preventDefault: () => void;
}) => {
    event.preventDefault();
};

const InputGroupButton: FC<IInputGroupButtonProps> = ({
    isShowWhileReadOnly = false,
    isDisabled: isDisabledProp,
    onHoverOut: onHoverOutProp,
    onPressOut: onPressOutProp,
    onHoverIn: onHoverInProp,
    onPressIn: onPressInProp,
    icon: IconComponentProp,
    isRefocusEnabled = true,
    isPressed: isToggled,
    onFocus: onFocusProp,
    onPress: onPressProp,
    onBlur: onBlurProp,
    type = "neutral",
    customTheme,
    style,
    title,
    ...props
}) => {
    const {
        customTheme: groupCustomTheme,
        isDisabled: isGroupDisabled,
        currentSize,
        focusInput,
        isReadOnly,
        isLoading,
        inputID,
        size
    } = useInputGroupContext();

    const addonContext = useInputGroupAddonContext();

    const currentTheme = customTheme ?? groupCustomTheme;

    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(currentTheme);

    const pointerType = usePointerType();

    const isDisabled = isGroupDisabled || !!isDisabledProp;

    const isIconAction = !!IconComponentProp;

    const isBlock = addonContext?.align === "blockStart" || addonContext?.align === "blockEnd";

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        onHoverOut: onHoverOutProp,
        onPressOut: onPressOutProp,
        onHoverIn: onHoverInProp,
        onPressIn: onPressInProp,
        onFocus: onFocusProp,
        onBlur: onBlurProp,
        isDisabled
    });

    const pressScaleStyle = usePressScale({
        customTheme: currentTheme,
        isDisabled,
        isPressed
    });

    const currentColors = getInputGroupButtonColors({
        isHighlighted: isHovered || isPressed,
        isSelected: !!isToggled,
        isIconAction,
        isDisabled,
        colors,
        type
    });

    const {
        labelVariant
    } = getControlMeasures({
        size: INPUT_GROUP_COMPACT_SIZES[size],
        controlPaddings,
        controlHeights,
        shapeConfigs,
        iconSizes
    });

    const {
        container: containerDynamicStyle,
        surface: surfaceDynamicStyle
    } = useStyles({
        isFocusVisible,
        currentColors,
        isIconAction,
        shapeConfigs,
        touchTargets,
        currentSize,
        pointerType,
        radiuses,
        borders,
        colors,
        spaces
    });

    const {
        containerStyle,
        surfaceStyle
    } = splitControlStyle({
        style
    });

    const handlePress = () => {
        if(onPressProp) {
            onPressProp();
        }

        if(isRefocusEnabled) {
            focusInput();
        }
    };

    if(isReadOnly && !isShowWhileReadOnly) {
        return null;
    }

    if(isBlock) {
        return <ButtonComponent
            {...props}
            size={INPUT_GROUP_COMPACT_SIZES[size] === "small" ? "small" : "medium"}
            customColor={isToggled ? currentColors.containerColor : undefined}
            aria-checked={Platform.OS === "web" ? undefined : isToggled}
            aria-pressed={Platform.OS === "web" ? isToggled : undefined}
            title={isIconAction ? undefined : title}
            customTheme={currentTheme}
            accessibilityLabel={title}
            icon={IconComponentProp}
            isDisabled={isDisabled}
            spreadBehaviour="free"
            onPress={handlePress}
            variant="ghost"
            type="neutral"
        />;
    }

    if(isLoading) {
        return <InputGroupSpinner />;
    }

    const webProps = Platform.OS === "web" ? {
        onMouseDown: preventActionFocusSteal,
        "aria-controls": inputID,
        "aria-pressed": isToggled
    } : {};

    return <Pressable
        {...props}
        {...webProps}
        {...interactionHandlers}
        aria-checked={Platform.OS === "web" ? undefined : isToggled}
        accessibilityLabel={title}
        aria-disabled={isDisabled}
        style={[
            containerStyle,
            stylesheet.container,
            containerDynamicStyle
        ]}
        disabled={isDisabled}
        onPress={handlePress}
        aria-label={title}
        role="button"
    >
        <Animated.View
            style={[
                surfaceStyle,
                stylesheet.surface,
                surfaceDynamicStyle,
                pressScaleStyle
            ]}
        >
            <StateLayer
                borderRadius={getInputGroupButtonRadius({
                    isIconAction,
                    radiuses,
                    borders,
                    spaces
                })}
                overlayColor={currentColors.overlayColor}
                customTheme={currentTheme}
                isPressed={isPressed}
                isHovered={isHovered}
                isSquircle={true}
            />
            {
                IconComponentProp ?
                    <IconComponentProp
                        customColor={currentColors.iconColor}
                        color={currentColors.iconColorKey}
                        size={currentSize.iconSize}
                    />
                :
                    <Text
                        customColor={currentColors.titleColor}
                        customTheme={currentTheme}
                        variant={labelVariant}
                        numberOfLines={1}
                    >
                        {title}
                    </Text>
            }
        </Animated.View>
    </Pressable>;
};
export default InputGroupButton;
